/*
  component: post-style-picker
  title: Post Style Picker
  methods:
    - getStyle: returns selected style object or null
    - getStylePayload: returns { postStyle, lastUsedStyleId } for create calls
    - getLastUsedStyleId: returns saved style id when a named style is loaded
    - setStyle: hydrate picker from saved style object
    - clearStyle: reset selection
    - showError: display inline error message
    - clearError: clear inline error message
  deps: collapsible-section
  events:
    - post-style-change: detail { style }
    - post-style-preview-request: when section expands
*/
import {
  meetsWcagAA,
  resolveStyleColors,
  STYLE_PRESET_COLORS
} from '../utils/post-style-contrast.js';

class PostStylePicker extends HTMLElement {
  constructor() {
    super();
    this.selectedStyle = null;
    this.lastUsedStyleId = null;
    this.savedStyles = [];
    this._colorsTouched = false;
    this._onClick = this._onClick.bind(this);
    this._onChange = () => this.applyCustomStyle();
    this._onColorInput = () => {
      this._colorsTouched = true;
      this.applyCustomStyle();
    };
    this._onCollapsibleToggle = this._onCollapsibleToggle.bind(this);
  }

  connectedCallback() {
    this.render();
    this.attachListeners();
    this.updateContrastHint();
    this.loadSavedStylesAndDefault();
  }

  getStyle() {
    return this.selectedStyle;
  }

  getLastUsedStyleId() {
    return this.lastUsedStyleId;
  }

  getStylePayload() {
    const style = this.getStyle();
    if (!style) return {};
    const payload = { postStyle: style };
    if (this.lastUsedStyleId) {
      payload.lastUsedStyleId = this.lastUsedStyleId;
    }
    return payload;
  }

  showError(message) {
    const el = this.querySelector('.post-style-error');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  clearError() {
    const el = this.querySelector('.post-style-error');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  async loadSavedStylesAndDefault() {
    try {
      const authData = await window.API.request('/auth/status');
      if (!authData.authenticated || !authData.user?.id) {
        return;
      }

      const [stylesRes, settingsRes] = await Promise.all([
        window.API.postStyles.list(),
        window.API.request(`/users/settings/${authData.user.id}`)
      ]);

      this.savedStyles = stylesRes.post_styles || [];
      this._renderSavedStylesList();

      const lastStyle = settingsRes?.last_post_style;
      const lastStyleId = settingsRes?.last_post_style_id;
      if (lastStyle && typeof lastStyle === 'object' && Object.keys(lastStyle).length > 0) {
        this.setStyle(lastStyle, lastStyleId || null);
      }
    } catch (error) {
      window.logger?.debug?.('Could not load saved post styles', error);
    }
  }

  setStyle(style, savedStyleId = null) {
    if (typeof style === 'string') {
      try {
        style = JSON.parse(style);
      } catch {
        style = null;
      }
    }

    if (!style || typeof style !== 'object' || Object.keys(style).length === 0) {
      this.selectedStyle = null;
      this.lastUsedStyleId = null;
    } else {
      this.selectedStyle = { ...style };
      this.lastUsedStyleId = savedStyleId;
    }

    this._colorsTouched = Boolean(style?.fgColor || style?.bgColor);
    this.clearError();
    this.render();
    this.attachListeners();
    this._hydrateControls();
    this._emitStyleChange();
    this._renderSavedStylesList();
  }

  clearStyle() {
    this.selectedStyle = null;
    this.lastUsedStyleId = null;
    this._colorsTouched = false;
    this.clearError();
    this.querySelectorAll('.post-style-preset-btn').forEach((btn) => btn.classList.remove('selected'));
    const fg = this.querySelector('#customFg');
    const bg = this.querySelector('#customBg');
    if (fg) fg.value = '#000000';
    if (bg) bg.value = '#ffffff';
    this._emitStyleChange();
    this._renderSavedStylesList();
  }

  _hydrateControls() {
    if (!this.selectedStyle) {
      return;
    }

    const style = this.selectedStyle;
    const presetColors = style.preset ? STYLE_PRESET_COLORS[style.preset] : null;

    if (style.preset) {
      const presetBtn = this.querySelector(`.post-style-preset-btn[data-preset="${style.preset}"]`);
      if (presetBtn) {
        presetBtn.classList.add('selected');
      }
    }

    const font = this.querySelector('#styleFont');
    if (font) {
      font.value = style.font || '';
    }

    const fontSize = this.querySelector('#styleFontSize');
    if (fontSize) {
      fontSize.value = style.fontSize || '';
    }

    const border = this.querySelector('#styleBorder');
    if (border) {
      border.value = style.border || 'none';
    }

    const fg = this.querySelector('#customFg');
    if (fg) {
      fg.value = style.fgColor || presetColors?.fgColor || '#000000';
    }

    const bg = this.querySelector('#customBg');
    if (bg) {
      bg.value = style.bgColor || presetColors?.bgColor || '#ffffff';
    }
  }

  attachListeners() {
    this.removeEventListener('click', this._onClick);
    this.addEventListener('click', this._onClick);

    this.querySelectorAll('select.form-control').forEach((el) => {
      el.removeEventListener('change', this._onChange);
      el.addEventListener('change', this._onChange);
    });

    this.querySelectorAll('input.form-control-color').forEach((el) => {
      el.removeEventListener('input', this._onColorInput);
      el.addEventListener('input', this._onColorInput);
    });

    const saveBtn = this.querySelector('#saveStyleBtn');
    if (saveBtn) {
      saveBtn.removeEventListener('click', this._onSaveStyle);
      this._onSaveStyle = this._onSaveStyle || this.handleSaveStyle.bind(this);
      saveBtn.addEventListener('click', this._onSaveStyle);
    }

    const section = this.querySelector('collapsible-section');
    if (section) {
      section.removeEventListener('collapsible-toggle', this._onCollapsibleToggle);
      section.addEventListener('collapsible-toggle', this._onCollapsibleToggle);
    }
  }

  _onCollapsibleToggle(e) {
    if (e.detail?.open) {
      this.dispatchEvent(new CustomEvent('post-style-preview-request', { bubbles: true }));
    }
  }

  _emitStyleChange() {
    this.updateContrastHint();
    this.dispatchEvent(new CustomEvent('post-style-change', {
      bubbles: true,
      detail: { style: this.selectedStyle }
    }));
  }

  updateContrastHint() {
    const contrastHint = this.querySelector('.post-style-contrast-hint');
    if (!contrastHint) return;

    if (!this.selectedStyle) {
      contrastHint.hidden = true;
      return;
    }

    const { fgColor, bgColor } = resolveStyleColors(this.selectedStyle);
    contrastHint.hidden = meetsWcagAA(fgColor, bgColor);
  }

  async handleSaveStyle() {
    const nameInput = this.querySelector('#saveStyleName');
    const name = nameInput?.value?.trim();
    if (!name) {
      this.showError('Give your style a name before saving.');
      return;
    }
    if (!this.selectedStyle) {
      this.showError('Pick a style to save first.');
      return;
    }

    const saveBtn = this.querySelector('#saveStyleBtn');
    if (saveBtn) saveBtn.disabled = true;

    try {
      const created = await window.API.postStyles.create({
        name,
        style: this.selectedStyle
      });
      this.savedStyles.unshift(created.post_style);
      if (nameInput) nameInput.value = '';
      this.lastUsedStyleId = created.post_style.id;
      this.clearError();
      this._renderSavedStylesList();
    } catch (error) {
      this.showError(error.message || 'Could not save style.');
    } finally {
      if (saveBtn) saveBtn.disabled = false;
    }
  }

  async handleDeleteSavedStyle(id) {
    if (!window.confirm('Delete this saved style?')) {
      return;
    }
    try {
      await window.API.postStyles.delete(id);
      this.savedStyles = this.savedStyles.filter((s) => s.id !== id);
      if (this.lastUsedStyleId === id) {
        this.lastUsedStyleId = null;
      }
      this._renderSavedStylesList();
    } catch (error) {
      this.showError(error.message || 'Could not delete style.');
    }
  }

  _renderSavedStylesList() {
    const list = this.querySelector('#savedStylesList');
    const section = this.querySelector('.post-style-saved-load-section');
    if (!list) return;

    if (this.savedStyles.length === 0) {
      list.innerHTML = '';
      list.hidden = true;
      if (section) section.hidden = true;
      return;
    }

    if (section) section.hidden = false;
    list.hidden = false;
    list.innerHTML = this.savedStyles.map((s) => `
      <div class="post-style-saved-item">
        <button type="button" class="btn btn-secondary btn-sm post-style-saved-load${this.lastUsedStyleId === s.id ? ' selected' : ''}" data-id="${s.id}">
          ${this.escapeHtml(s.name)}
        </button>
        <button type="button" class="btn btn-secondary btn-sm post-style-saved-delete" data-id="${s.id}" aria-label="Delete ${this.escapeHtml(s.name)}">
          🗑️
        </button>
      </div>
    `).join('');

    list.querySelectorAll('.post-style-saved-load').forEach((btn) => {
      btn.addEventListener('click', () => {
        const saved = this.savedStyles.find((s) => s.id === parseInt(btn.dataset.id, 10));
        if (saved) {
          const style = typeof saved.style === 'string' ? JSON.parse(saved.style) : saved.style;
          this.setStyle(style, saved.id);
        }
      });
    });

    list.querySelectorAll('.post-style-saved-delete').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.handleDeleteSavedStyle(parseInt(btn.dataset.id, 10));
      });
    });
  }

  escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _onClick(e) {
    const presetBtn = e.target.closest('.post-style-preset-btn');
    if (presetBtn) {
      this.querySelectorAll('.post-style-preset-btn').forEach((btn) => btn.classList.remove('selected'));
      presetBtn.classList.add('selected');
      const preset = presetBtn.dataset.preset;
      this.selectedStyle = { preset };
      this.lastUsedStyleId = null;
      this._colorsTouched = false;
      this.clearError();

      const presetColors = STYLE_PRESET_COLORS[preset];
      const fg = this.querySelector('#customFg');
      const bg = this.querySelector('#customBg');
      if (fg && presetColors) fg.value = presetColors.fgColor;
      if (bg && presetColors) bg.value = presetColors.bgColor;

      this._emitStyleChange();
      this._renderSavedStylesList();
    }
  }

  applyCustomStyle() {
    const preset = this.querySelector('.post-style-preset-btn.selected')?.dataset.preset;
    const font = this.querySelector('#styleFont')?.value;
    const fontSize = this.querySelector('#styleFontSize')?.value;
    const border = this.querySelector('#styleBorder')?.value;
    const fgColor = this.querySelector('#customFg')?.value;
    const bgColor = this.querySelector('#customBg')?.value;

    this.selectedStyle = {};
    if (preset) this.selectedStyle.preset = preset;
    if (font) this.selectedStyle.font = font;
    if (fontSize) this.selectedStyle.fontSize = fontSize;
    if (border && border !== 'none') this.selectedStyle.border = border;

    const includeColors = this._colorsTouched || !preset;
    if (includeColors && fgColor) this.selectedStyle.fgColor = fgColor;
    if (includeColors && bgColor) this.selectedStyle.bgColor = bgColor;

    if (Object.keys(this.selectedStyle).length === 0) {
      this.selectedStyle = null;
    }

    this.lastUsedStyleId = null;
    this.clearError();
    this._emitStyleChange();
    this._renderSavedStylesList();
  }

  render() {
    this.innerHTML = `
      <collapsible-section
        summary="🎨 Post Style (optional)"
        hint="Expand to customize how this post looks. Leave collapsed for the default style."
      >
        <div class="post-style-picker-body">
          <div class="post-style-error alert alert-error" hidden role="alert"></div>
          <div class="post-style-saved-load-section" hidden>
            <div id="savedStylesList" class="post-style-saved-list" hidden></div>
          </div>
          <div class="post-style-presets" role="group" aria-label="Style presets">
            <button type="button" class="btn btn-secondary btn-sm post-style-preset-btn" data-preset="retro-terminal">Retro Terminal</button>
            <button type="button" class="btn btn-secondary btn-sm post-style-preset-btn" data-preset="pastel-dreams">Pastel Dreams</button>
            <button type="button" class="btn btn-secondary btn-sm post-style-preset-btn" data-preset="neon-nights">Neon Nights</button>
            <button type="button" class="btn btn-secondary btn-sm post-style-preset-btn" data-preset="fourth-of-july">🎆 Fourth of July</button>
          </div>
          <div class="form-group">
            <label class="form-label" for="styleFont">Font</label>
            <select id="styleFont" class="form-control">
              <option value="">Default</option>
              <option value="arial">Arial</option>
              <option value="georgia">Georgia</option>
              <option value="courier">Courier</option>
              <option value="comic">Comic Sans</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label" for="styleFontSize">Size</label>
            <select id="styleFontSize" class="form-control">
              <option value="">Normal</option>
              <option value="small">Small</option>
              <option value="large">Large</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label" for="styleBorder">Border</label>
            <select id="styleBorder" class="form-control">
              <option value="none">None</option>
              <option value="soft">Soft</option>
              <option value="dashed">Dashed</option>
              <option value="neon">Neon</option>
              <option value="retro">Retro</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label" for="customFg">Text color</label>
            <input type="color" id="customFg" class="form-control form-control-color" value="#000000" />
          </div>
          <div class="form-group">
            <label class="form-label" for="customBg">Background</label>
            <input type="color" id="customBg" class="form-control form-control-color" value="#ffffff" />
          </div>
          <p class="form-text post-style-contrast-hint" hidden>
            Heads up: these colors may be hard to read for some viewers.
          </p>
          <div class="post-style-save-row">
            <input type="text" id="saveStyleName" class="form-control" placeholder="Style name…" maxlength="100" />
            <button type="button" id="saveStyleBtn" class="btn btn-secondary btn-sm">Save style</button>
          </div>
        </div>
      </collapsible-section>
    `;
  }
}

customElements.define('post-style-picker', PostStylePicker);
